'use client';

import { detectDevice, isResponsivePage } from '@/shared/utils/deviceDetection';
import { usePathname } from 'next/navigation';
import { useEffect, useLayoutEffect } from 'react';

// 브라우저 환경에서는 useLayoutEffect, 서버에서는 useEffect 사용
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const VIEWPORT_CONFIG = {
  mobileNonResponsive: {
    content:
      'width=1920, initial-scale=0.2, minimum-scale=0.1, maximum-scale=1.0, user-scalable=yes',
    bodyStyle: { minWidth: '1920px', overflowX: 'auto' },
  },
  responsive: {
    content:
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
    bodyStyle: { minWidth: 'unset', overflowX: 'unset' },
  },
  default: {
    content:
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no',
    bodyStyle: { minWidth: '1920px', overflowX: 'auto' },
  },
} as const;

export function ViewportManager() {
  const pathname = usePathname();

  useIsomorphicLayoutEffect(() => {
    const setViewport = () => {
      const { isMobile } = detectDevice();
      const isPageResponsive = isResponsivePage(pathname);

      let viewport = document.querySelector(
        'meta[name="viewport"]',
      ) as HTMLMetaElement;

      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.name = 'viewport';
        document.head.appendChild(viewport);
      }

      // 설정 선택
      const config = (() => {
        if (isMobile && isPageResponsive) {
          return VIEWPORT_CONFIG.responsive;
        } else if (isMobile && !isPageResponsive) {
          return VIEWPORT_CONFIG.mobileNonResponsive;
        }
        return VIEWPORT_CONFIG.default;
      })()

      // 스타일 적용
      viewport.content = config.content;
      Object.assign(document.body.style, config.bodyStyle);

      // 모바일 가로 스크롤 중앙 정렬
      if (isMobile && !isPageResponsive) {
        const centerScroll = () => {
          const contentWidth = 1920;
          const viewportWidth = window.visualViewport?.width || window.innerWidth;
          const scrollLeft = (contentWidth - viewportWidth) / 2;

          window.scrollTo(scrollLeft, 0);
        };

        // 페이지 로드 후 진행
        setTimeout(centerScroll, 100);
        window.addEventListener('load', centerScroll);
      }
    };

    // DOM이 준비되면 즉시 실행
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setViewport);
      return () => {
        document.removeEventListener('DOMContentLoaded', setViewport);
      };
    } else {
      setViewport();
    }
  }, [pathname]);

  return null;
}